import { 
  ForwardedRef, 
  forwardRef, 
  useEffect, 
  useImperativeHandle, 
  useRef, 
  useState 
} from "react";
import styles from "./index.module.scss";
import { TimeLabel } from "@/components";
import { getFixedNum } from "@/utils";
import { TRangeSelectHandle } from "@/types";

type RangeSelectProps = {
  minRange: number;
  maxRange: number;
  start: number;
  end: number;
  getDuration: () => number;
  onChange: (start: number, end: number) => void;
  onControl?: () => void;
  onOutOfControl?: () => void;

  range?: any;
}

const RangeSelect = (props: RangeSelectProps, ref: ForwardedRef<TRangeSelectHandle>) => {
  const {
    minRange,
    maxRange,
    start,
    end,
    getDuration,
    onChange,
    onControl,
    onOutOfControl,

    range,
  } = props;

  const isFocusRangeBarRef = useRef(false);
  const isFocusLeftBarRef = useRef(false);
  const isFocusRightBarRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastX = useRef(0);
  const leftRef = useRef(0);
  const rightRef = useRef(0);
  const [, refresh] = useState([]);

  const sync = (target: { left: number; right: number }) => {
    const duration = getDuration();    
    const { left, right } = target;
    const total = rootRef.current?.clientWidth!;
    const startPerc = left / total;
    const startTime = duration * startPerc;
    const endPerc = (total - right) / total;
    const endTime = duration * endPerc;
    const deltaTime = Math.floor(endTime - startTime);
    if (deltaTime >= minRange && deltaTime <= maxRange) {
      leftRef.current = left;
      rightRef.current = right;
      onChange(getFixedNum(startTime, 3), getFixedNum(endTime, 3));
      refresh([]);
    }
  }

  const isSinglePointer = (e: any) => {    
    if (e.touches) {
      return e.touches.length === 1;
    } else {
      return true;
    }
  }

  const getX = (e: any) => {
    let x = 0;
    if (e.touches) {
      x = e.touches[0].pageX 
    } else {
      x = e.pageX;
    }
    return x;
  }

  const focusRangeBar = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    isFocusRangeBarRef.current = true;
    lastX.current = getX(e);
    onControl?.();
  }

  const focusLeftBar = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    isFocusLeftBarRef.current = true;
    lastX.current = getX(e);
    onControl?.();
  }

  const focusRightBar = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    isFocusRightBarRef.current = true;
    lastX.current = getX(e);
    onControl?.();
  }

  const rangeBarMove = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    if (isFocusRangeBarRef.current) {      
      const currentX = getX(e);
      const delta = currentX - lastX.current;
      
      let target = {
        left: leftRef.current,
        right: rightRef.current
      };
      if (delta > 0) {
        const temp = rightRef.current - delta;
        if (temp >= 0) {
          target = {
            left: leftRef.current + delta,
            right: rightRef.current - delta
          };
        } else {
          target = {
            left: leftRef.current + rightRef.current,
            right: 0
          };
        }
      } else if (delta < 0) {
        const temp = leftRef.current + delta;
        if (temp >= 0) {
          target = {
            left: leftRef.current + delta,
            right: rightRef.current - delta
          };
        } else {
          target = {
            left: 0,
            right: leftRef.current + rightRef.current
          };
        }
      }       
      sync(target);

      lastX.current = currentX;
    } 
  }

  const leftBarMove = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    if (isFocusLeftBarRef.current) {
      const currentX = getX(e);
      const delta = currentX - lastX.current;
      
      const total = rootRef.current!.clientWidth;
      const target = {
        left: Math.min(Math.max(0, leftRef.current + delta), total),
        right: rightRef.current
      };
      sync(target);

      lastX.current = currentX;
    } 
  }

  const rightBarMove = (e: any) => {
    e.preventDefault();
    if (!isSinglePointer(e)) {
      return;
    }
    if (isFocusRightBarRef.current) {
      const currentX = getX(e);
      const delta = currentX - lastX.current;

      const total = rootRef.current!.clientWidth;
      const target = {
        left: leftRef.current,
        right: Math.min(Math.max(0, rightRef.current - delta), total)
      };            
      sync(target);

      lastX.current = currentX;
    }
  }

  const blurRangeBar = (e: any) => {
    if (isFocusRangeBarRef.current) {
      isFocusRangeBarRef.current = false;
      onOutOfControl?.();
    } 
  }

  const blurLeftBar = (e: any) => {
    if (isFocusLeftBarRef.current) {
      isFocusLeftBarRef.current = false;
      onOutOfControl?.();
    } 
  }

  const blurRightBar = (e: any) => {
    if (isFocusRightBarRef.current) {
      isFocusRightBarRef.current = false;
      onOutOfControl?.();
    } 
  }

  useImperativeHandle(ref, () => {
    return {
      init: (duration: number, end: number) => {
        const perc = end / duration;
        const total = rootRef.current?.clientWidth!;
        rightRef.current = total * (1 - perc);
        refresh([]);
      }
    };
  }, []);

  // bar move
  useEffect(() => {
    document.addEventListener("mousemove", rangeBarMove);
    document.addEventListener("mousemove", leftBarMove);
    document.addEventListener("mousemove", rightBarMove);

    document.addEventListener("touchmove", rangeBarMove);
    document.addEventListener("touchmove", leftBarMove);
    document.addEventListener("touchmove", rightBarMove);

    return () => {
      document.removeEventListener("mousemove", rangeBarMove);
      document.removeEventListener("mousemove", leftBarMove);
      document.removeEventListener("mousemove", rightBarMove);

      document.removeEventListener("touchmove", rangeBarMove);
      document.removeEventListener("touchmove", leftBarMove);
      document.removeEventListener("touchmove", rightBarMove);
    }
  }, []);

  // blur bar
  useEffect(() => {
    document.addEventListener("mouseup", blurRangeBar);
    document.addEventListener("mouseup", blurLeftBar);
    document.addEventListener("mouseup", blurRightBar);

    document.addEventListener("touchend", blurRangeBar);
    document.addEventListener("touchend", blurLeftBar);
    document.addEventListener("touchend", blurRightBar);

    return () => {
      document.removeEventListener("mouseup", blurRangeBar);
      document.removeEventListener("mouseup", blurLeftBar);
      document.removeEventListener("mouseup", blurRightBar);

      document.removeEventListener("touchend", blurRangeBar);
      document.removeEventListener("touchend", blurLeftBar);
      document.removeEventListener("touchend", blurRightBar);
    }
  }, []);

  return (
    <div
      className={styles["range_select"]}
      ref={rootRef}
    >
      <div
        className={styles["left_mask"]}
        style={{
          right: rootRef.current ? rootRef.current.clientWidth - leftRef.current : 0
        }}
      >
      </div>

      <div
        className={styles["left_bar"]}
        style={{
          left: leftRef.current,
        }}
        onMouseDown={focusLeftBar}
        onTouchStart={focusLeftBar}
      >
        <TimeLabel
          value={start}
        ></TimeLabel>
      </div>

      <div
        className={styles["range_bar"]}
        style={{
          left: leftRef.current,
          right: rightRef.current,
        }}
        onMouseDown={focusRangeBar}
        onTouchStart={focusRangeBar}
      >
        {range}
      </div>

      <div
        className={styles["right_bar"]}
        style={{
          right: rightRef.current,
        }}
        onMouseDown={focusRightBar}
        onTouchStart={focusRightBar}
      >
        <TimeLabel
          value={end}
        ></TimeLabel>
      </div>

      <div
        className={styles["right_mask"]}
        style={{
          left: rootRef.current ? rootRef.current.clientWidth - rightRef.current : 0
        }}
      >
      </div>
    </div>
  )
}

export default forwardRef(RangeSelect);